import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";

function About() {
  const values = [
    {
      title: "Natural by default",
      description:
        "We choose traceable wool and cotton for their warmth, breathability and ability to age with character.",
    },
    {
      title: "Chosen with care",
      description:
        "Our Hong Kong team selects versatile pieces with close attention to comfort, finish and everyday usefulness.",
    },
    {
      title: "Colour with feeling",
      description:
        "Seasonal palettes are built to be mixed, repeated and worn for years rather than tied to a single moment.",
    },
  ];

  const process = [
    {
      title: "Begin with the fibre",
      description:
        "We select natural yarns for softness, strength and an honest hand-feel, accepting the small variations that make them distinct.",
    },
    {
      title: "Develop slowly",
      description:
        "Shapes are sampled, worn and adjusted until proportion and movement feel effortless in everyday life.",
    },
    {
      title: "Make in considered runs",
      description:
        "Limited production helps our workshop partners plan carefully and lets each garment receive the time it needs.",
    },
  ];

  return (
    <main className="content-page editorial-page about" id="main-content">
      <div className="content-page__inner editorial-page__inner">
        <header className="content-page__header editorial-page__hero">
          <p className="content-page__eyebrow">Our story</p>
          <h1 className="content-page__title">About {siteConfig.brandName}</h1>
          <p className="content-page__intro">{siteConfig.tagline}</p>
          <p className="editorial-page__lede">{siteConfig.description}</p>
        </header>

        <section className="editorial-page__section" aria-labelledby="story-title">
          <div className="editorial-page__section-heading">
            <p className="content-page__eyebrow">Since the beginning</p>
            <h2 id="story-title">Clothes to live in</h2>
          </div>
          <div className="editorial-page__copy">
            <p>
              {siteConfig.brandName} began with a simple idea: everyday knitwear can
              feel joyful, useful and deeply personal. We design relaxed silhouettes
              that leave room for movement and become more familiar with every wear.
            </p>
            <p>
              Rather than chase constant novelty, we return to enduring forms and
              explore them through fibre, proportion and colour. The result is a
              wardrobe intended to be layered, shared, repaired and kept.
            </p>
          </div>
        </section>

        <section
          className="editorial-page__section editorial-page__section--values"
          aria-labelledby="values-title"
        >
          <div className="editorial-page__section-heading">
            <p className="content-page__eyebrow">What guides us</p>
            <h2 id="values-title">Our values</h2>
          </div>
          <div className="editorial-page__grid">
            {values.map((value, index) => (
              <article className="editorial-page__card" key={value.title}>
                <span className="editorial-page__index" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          className="editorial-page__section editorial-page__section--process"
          aria-labelledby="process-title"
        >
          <div className="editorial-page__section-heading">
            <p className="content-page__eyebrow">From yarn to wardrobe</p>
            <h2 id="process-title">How we make</h2>
          </div>
          <ol className="editorial-page__steps">
            {process.map((step) => (
              <li className="editorial-page__step" key={step.title}>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="editorial-page__section editorial-page__section--materials"
          id="materials"
          aria-labelledby="materials-title"
        >
          <div className="editorial-page__section-heading">
            <p className="content-page__eyebrow">Materials</p>
            <h2 id="materials-title">Naturally individual</h2>
          </div>
          <div className="editorial-page__copy">
            <p>
              Natural fibres respond to their surroundings. Subtle differences in
              texture and shade are part of the garment, not faults, and give every
              piece its own expression.
            </p>
            <p>
              Fibre composition and origin are listed on each product page so you can
              make an informed choice before adding a piece to your wardrobe.
            </p>
          </div>
        </section>

        <section
          className="editorial-page__section editorial-page__section--care"
          id="care"
          aria-labelledby="care-title"
        >
          <div className="editorial-page__section-heading">
            <p className="content-page__eyebrow">Care</p>
            <h2 id="care-title">Wear more, wash less</h2>
          </div>
          <div className="editorial-page__copy">
            <p>
              Air knitwear between wears, remove pills gently and fold it for storage.
              When washing is needed, follow the garment label and reshape while damp.
              Thoughtful care preserves softness and helps natural fibres last longer.
            </p>
          </div>
        </section>

        <aside className="editorial-page__cta" aria-labelledby="about-cta-title">
          <p className="content-page__eyebrow">Meet the collection</p>
          <h2 id="about-cta-title">Made for everyday rituals</h2>
          <div className="editorial-page__actions">
            <Link className="editorial-page__button" to="/shop">
              Shop all pieces
            </Link>
            <Link
              className="editorial-page__button editorial-page__button--secondary"
              to="/contact"
            >
              Ask us anything
            </Link>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default About;
